import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Upload,
  message,
  Typography,
  Divider,
  Alert,
  UploadFile,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import RobinPage from "../components/RobinPage";
import { useEffect, useState } from "react";
import { PlusOutlined, SendOutlined, UserOutlined, BellOutlined } from "@ant-design/icons";
import { useUploadFileMutation } from "../graphql/generated/hooks";
import { 
  useGetCustomerUsers, 
  useSendNotificationToUser,
  CustomerUser,
  SendNotificationResponse,
  NotificationTitle,
  NotificationBody
} from "../hooks/useNotification";

const { TextArea } = Input;
const { Title, Text } = Typography;

function NotificationSend() {
  const { t } = useTranslation();
  const [form] = useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadedImageId, setUploadedImageId] = useState<number | null>(null);

  // GraphQL hooks
  const { data: usersData, loading: loadingUsers } = useGetCustomerUsers();
  const [sendNotification, { loading: sending }] = useSendNotificationToUser();
  const [uploadFile, { loading: uploading }] = useUploadFileMutation();

  const users: CustomerUser[] = usersData?.getCustomerUsers || [];

  // Convert file to base64
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  };

  // Handle image upload
  const handleCustomUpload = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;
    
    try {
      onProgress({ percent: 0 });
      
      // Convert file to base64
      const base64String = await convertFileToBase64(file);
      onProgress({ percent: 50 });
      
      // Upload using GraphQL mutation
      const result = await uploadFile({
        variables: {
          base64File: base64String,
          originalName: file.name,
          folder: 'notifications',
        },
      });
      
      onProgress({ percent: 100 });
      
      if (result.data?.uploadFile) {
        setUploadedImageId(result.data.uploadFile.id);
        onSuccess({
          id: result.data.uploadFile.id,
          url: result.data.uploadFile.url,
        }, file);
      } else {
        onError(new Error('Upload failed: No data returned'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      onError(error);
    }
  };

  const handleRemove = () => {
    setUploadedImageId(null);
    setFileList([]);
  };

  const onFinish = async (values: any) => {
    try {
      // Parse data as JSON if provided, otherwise use empty object
      let parsedData = {};
      if (values.data && values.data.trim()) {
        try {
          parsedData = JSON.parse(values.data);
        } catch (parseError) {
          messageApi.error("Invalid JSON format in additional data field");
          return;
        }
      }

      const result = await sendNotification({
        variables: {
          userIds: values.userIds || null,
          titleAr: values.titleAr,
          titleEn: values.titleEn,
          bodyAr: values.bodyAr,
          bodyEn: values.bodyEn,
          data: parsedData,
          imageId: uploadedImageId || undefined,
        },
      });

      const notificationResult = result.data?.sendNotificationToUser;

      if (notificationResult?.success) {
        messageApi.success(
          `Notification sent successfully! ${notificationResult.successCount} users notified.`
        );
        form.resetFields();
        setFileList([]);
        setUploadedImageId(null);
      } else {
        messageApi.error(
          notificationResult?.message || "Failed to send notification"
        );
      }
    } catch (error) {
      console.error("Send notification error:", error);
      messageApi.error("Failed to send notification");
    }
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload Image</div>
    </div>
  );

  return (
    <RobinPage title="Send Push Notification">
      <Row justify="center">
        {contextHolder}
        <Col xs={24} sm={20} md={16} lg={12} xl={10}>
          <Card
            title={
              <Space>
                <BellOutlined style={{ color: '#1890ff' }} />
                <Title level={4} style={{ margin: 0 }}>
                  Send Push Notification
                </Title>
              </Space>
            }
            bordered={false}
            style={{
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              borderRadius: '12px',
            }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              name="sendNotification"
              size="large"
            >
              {/* Notification Title - English */}
              <Form.Item
                name="titleEn"
                label={
                  <Text strong>
                    <BellOutlined /> Notification Title (English)
                  </Text>
                }
                rules={[
                  { required: true, message: "Please enter English notification title" },
                  { max: 100, message: "Title must be less than 100 characters" },
                ]}
              >
                <Input
                  placeholder="Enter notification title in English"
                  showCount
                  maxLength={100}
                />
              </Form.Item>

              {/* Notification Title - Arabic */}
              <Form.Item
                name="titleAr"
                label={
                  <Text strong>
                    <BellOutlined /> Notification Title (Arabic)
                  </Text>
                }
                rules={[
                  { required: true, message: "Please enter Arabic notification title" },
                  { max: 100, message: "Title must be less than 100 characters" },
                ]}
              >
                <Input
                  placeholder="أدخل عنوان الإشعار بالعربية"
                  showCount
                  maxLength={100}
                  dir="rtl"
                />
              </Form.Item>

              {/* Notification Body - English */}
              <Form.Item
                name="bodyEn"
                label={
                  <Text strong>
                    📝 Notification Message (English)
                  </Text>
                }
                rules={[
                  { required: true, message: "Please enter English notification message" },
                  { max: 500, message: "Message must be less than 500 characters" },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Enter your notification message in English..."
                  showCount
                  maxLength={500}
                />
              </Form.Item>

              {/* Notification Body - Arabic */}
              <Form.Item
                name="bodyAr"
                label={
                  <Text strong>
                    📝 Notification Message (Arabic)
                  </Text>
                }
                rules={[
                  { required: true, message: "Please enter Arabic notification message" },
                  { max: 500, message: "Message must be less than 500 characters" },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="أدخل رسالة الإشعار بالعربية..."
                  showCount
                  maxLength={500}
                  dir="rtl"
                />
              </Form.Item>

              {/* Additional Data */}
              <Form.Item
                name="data"
                label={
                  <Text strong>
                    🔗 Additional Data (Optional)
                  </Text>
                }
                extra="JSON string or any additional data to send with the notification"
              >
                <TextArea
                  rows={2}
                  placeholder='{"action": "open_page", "page": "products", "id": 123}'
                />
              </Form.Item>

              <Divider />

              {/* Image Upload */}
              <Form.Item
                label={
                  <Text strong>
                    🖼️ Notification Image (Optional)
                  </Text>
                }
                extra="Upload an image to display with your notification"
              >
                <Upload
                  customRequest={handleCustomUpload}
                  listType="picture-card"
                  fileList={fileList}
                  onRemove={handleRemove}
                  onChange={({ fileList }) => setFileList(fileList)}
                  accept="image/*"
                  maxCount={1}
                >
                  {fileList.length >= 1 ? null : uploadButton}
                </Upload>
              </Form.Item>

              <Divider />

              {/* Target Users */}
              <Form.Item
                name="userIds"
                label={
                  <Text strong>
                    <UserOutlined /> Target Users (Optional)
                  </Text>
                }
                extra="Leave empty to send to all users, or select specific users"
              >
                <Select
                  mode="multiple"
                  placeholder="Select users to notify (leave empty for all users)"
                  loading={loadingUsers}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={users.map((user) => ({
                    label: user.name || user.phoneNumber,
                    value: user.id,
                    key: user.id,
                  }))}
                  style={{ width: '100%' }}
                />
              </Form.Item>

              {/* Info Alert */}
              <Alert
                message="Notification Preview"
                description={
                  <div>
                    <Text strong>Title (EN):</Text> {form.getFieldValue('titleEn') || 'Your notification title'}<br />
                    <Text strong>Title (AR):</Text> {form.getFieldValue('titleAr') || 'عنوان الإشعار'}<br />
                    <Text strong>Message (EN):</Text> {form.getFieldValue('bodyEn') || 'Your notification message'}<br />
                    <Text strong>Message (AR):</Text> {form.getFieldValue('bodyAr') || 'رسالة الإشعار'}
                    {uploadedImageId && (
                      <>
                        <br />
                        <Text strong>Image:</Text> ✅ Image attached
                      </>
                    )}
                  </div>
                }
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />

              {/* Action Buttons */}
              <Form.Item style={{ marginBottom: 0 }}>
                <Space size="middle" style={{ width: '100%', justifyContent: 'center' }}>
                  <Button
                    size="large"
                    onClick={() => navigate(-1)}
                    style={{ minWidth: 120 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={sending || uploading}
                    icon={<SendOutlined />}
                    size="large"
                    style={{
                      minWidth: 120,
                      background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                      border: 'none',
                    }}
                  >
                    Send Notification
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </RobinPage>
  );
}

export default NotificationSend;
